import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Stack
} from '@mui/material';

import {
  LocalizationProvider
} from '@mui/x-date-pickers/LocalizationProvider';

import {
  AdapterDayjs
} from '@mui/x-date-pickers/AdapterDayjs';

import {
  ORDER_TYPES
} from '../TemplatesModal/OrdersDialog';

import VisitDateSection from './VisitDateSection';
import VitalsSection from './VitalsSection';
import TemplateSection from './TemplateSection';
import AssessmentActions from './AssessmentActions';
import AssessmentPlanSection from './AssessmentPlanSection';
import SoapFooterActions from './SoapFooterActions';
import SoapEntryDialogs from './SoapEntryDialogs';

import {
  initialSoapText,
  initialVitals
} from './SoapEntryData';

import {
  soapEntryRootSx
} from './SoapEntryStyles';

const SoapEntry = ({
  isPast,
  setPast,
  onSaveEncounter,
  onSignEncounter
}) => {
  const [route, setRoute] = useState('');
  const [visitDate, setVisitDate] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const [vitals, setVitals] = useState(initialVitals);
  const [soapText, setSoapText] = useState(initialSoapText);

  const [openVSHx, setOpenVSHx] = useState(false);
  const [openSubjective, setOpenSubjective] = useState(false);
  const [openObjective, setOpenObjective] = useState(false);
  const [openICD10Diagnosis, setOpenICD10Diagnosis] = useState(false);

  const [
    openAssessmentTemplates,
    setOpenAssessmentTemplates
  ] = useState(false);

  const [
    activeOrderTemplateType,
    setActiveOrderTemplateType
  ] = useState(null);

  const [
    selectedICD10Diagnoses,
    setSelectedICD10Diagnoses
  ] = useState([]);

  const [openRX, setOpenRX] = useState(false);
  const [signedPrescription, setSignedPrescription] = useState(null);

  const [openLabOrders, setOpenLabOrders] = useState(false);
  const [signedLabOrder, setSignedLabOrder] = useState(null);

  const [openImageOrders, setOpenImageOrders] = useState(false);
  const [signedImageOrder, setSignedImageOrder] = useState(null);

  const [
    openPhysicianOrders,
    setOpenPhysicianOrders
  ] = useState(false);

  const [
    signedPhysicianOrder,
    setSignedPhysicianOrder
  ] = useState(null);

  const [orderTemplateText, setOrderTemplateText] = useState({
    [ORDER_TYPES.LAB]: '',
    [ORDER_TYPES.IMAGING]: '',
    [ORDER_TYPES.PHYSICIAN]: ''
  });

  const markDirty = () => {
    setIsDirty(true);
  };

  const handleVitalChange = (key, value) => {
    setVitals(previous => ({
      ...previous,
      [key]: value
    }));

    markDirty();
  };

  const handleSoapChange = (key, value) => {
    setSoapText(previous => ({
      ...previous,
      [key]: value
    }));

    markDirty();
  };

  const handleVisitDateChange = value => {
    setVisitDate(value);
    markDirty();
  };

  const handleRouteChange = event => {
    setRoute(event.target.value);
    markDirty();
  };

  const saveOrder = (
    payload,
    setSignedOrder,
    setDialogOpen
  ) => {
    setSignedOrder(payload);
    setDialogOpen(false);
    markDirty();
  };

  const handlePrescriptionSign = payload => {
    setSignedPrescription(payload);
    setOpenRX(false);
    markDirty();
  };

  const handleTemplateOpen = type => {
    setActiveOrderTemplateType(type);
    setOpenAssessmentTemplates(true);
  };

  const buildCurrentEncounter = status => ({
    id: visitDate
      ? visitDate.valueOf()
      : Date.now(),

    date: visitDate
      ? visitDate.format('MM/DD/YYYY')
      : '',

    patientName: 'Willow, Gwen Marry',
    patientInfo: '3/06/2006 (20) yrs',

    vitals,

    subjective: soapText.Subjective,
    objective: soapText.Objective,
    assessmentPlan: soapText.AssessmentPlan,

    diagnoses: selectedICD10Diagnoses,

    prescription: signedPrescription || {
      medications: [],
      comments: ''
    },

    labOrder: signedLabOrder,
    imagingOrder: signedImageOrder,
    physicianOrder: signedPhysicianOrder,

    route,
    status,

    signedBy:
      status === 'signed'
        ? 'Dr. Charles'
        : ''
  });

  const handleSave = () => {
    onSaveEncounter(
      buildCurrentEncounter('draft')
    );

    setIsDirty(false);
  };

  const handleSaveAndSign = () => {
    onSignEncounter(
      buildCurrentEncounter('signed')
    );

    setIsDirty(false);
  };

  const assessmentActions = [
    {
      label: 'RX',
      type: 'rx',
      onClick: () => setOpenRX(true)
    },
    {
      label: 'LAB ORDERS',
      type: 'blue',
      onClick: () => setOpenLabOrders(true)
    },
    {
      label: 'IMAGING ORDERS',
      type: 'lightblue',
      onClick: () => setOpenImageOrders(true)
    },
    {
      label: 'PHYSICIAN ORDERS',
      type: 'gray',
      onClick: () => setOpenPhysicianOrders(true)
    }
  ];

  const dialogState = {
    openVSHx,
    openSubjective,
    openObjective,
    openAssessmentTemplates,
    openICD10Diagnosis,
    openRX,
    openLabOrders,
    openImageOrders,
    openPhysicianOrders,

    selectedICD10Diagnoses,
    signedLabOrder,
    signedImageOrder,
    signedPhysicianOrder,
    orderTemplateText
  };

  const dialogHandlers = {
    closeVSHx: () => setOpenVSHx(false),

    closeSubjective: () => setOpenSubjective(false),

    closeObjective: () => setOpenObjective(false),

    closeAssessmentTemplates: () =>
      setOpenAssessmentTemplates(false),

    closeDiagnosis: () =>
      setOpenICD10Diagnosis(false),

    closeRX: () => setOpenRX(false),

    closeLab: () => setOpenLabOrders(false),

    closeImaging: () => setOpenImageOrders(false),

    closePhysician: () =>
      setOpenPhysicianOrders(false),

    signPrescription: handlePrescriptionSign,

    signLab: payload =>
      saveOrder(
        payload,
        setSignedLabOrder,
        setOpenLabOrders
      ),

    signImaging: payload =>
      saveOrder(
        payload,
        setSignedImageOrder,
        setOpenImageOrders
      ),

    signPhysician: payload =>
      saveOrder(
        payload,
        setSignedPhysicianOrder,
        setOpenPhysicianOrders
      ),

    openTemplate: handleTemplateOpen,

    saveDiagnosis: diagnoses => {
      setSelectedICD10Diagnoses(diagnoses);
      setOpenICD10Diagnosis(false);
      markDirty();
    },

    saveSubjective: data => {
      let selectedText = '';

      if (typeof data.text === 'string') {
        selectedText = data.text;
      } else {
        const selectedRows =
          data.templateText?.[
            data.selectedTopicId
          ] || [];

        selectedText = selectedRows
          .filter(item => item.checked)
          .map(item => item.text)
          .join('\n');
      }

      handleSoapChange(
        'Subjective',
        selectedText
      );

      if (
        !Object.prototype.hasOwnProperty.call(
          data,
          'text'
        )
      ) {
        setOpenSubjective(false);
      }
    },

    saveObjective: ({ text }) => {
      handleSoapChange('Objective', text);
      setOpenObjective(false);
    },

    saveAssessmentTemplate: ({ text }) => {
      if (activeOrderTemplateType) {
        setOrderTemplateText(previous => ({
          ...previous,
          [activeOrderTemplateType]: text
        }));
      } else {
        handleSoapChange(
          'AssessmentPlan',
          text
        );
      }

      setOpenAssessmentTemplates(false);
      markDirty();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={soapEntryRootSx}>
        <Stack spacing={0.8}>
          <VisitDateSection
            isPast={isPast}
            visitDate={visitDate}
            onVisitDateChange={handleVisitDateChange}
            onExpandPast={setPast}
          />

          <VitalsSection
            vitals={vitals}
            onChange={handleVitalChange}
            onOpenHistory={() => setOpenVSHx(true)}
          />

          <TemplateSection
            label="Subjective"
            buttonText="Sub Template"
            value={soapText.Subjective}
            onChange={value =>
              handleSoapChange(
                'Subjective',
                value
              )
            }
            onTemplateOpen={() =>
              setOpenSubjective(true)
            }
          />

          <TemplateSection
            label="Objective"
            buttonText="Obj Template"
            value={soapText.Objective}
            onChange={value =>
              handleSoapChange(
                'Objective',
                value
              )
            }
            onTemplateOpen={() =>
              setOpenObjective(true)
            }
          />

          <AssessmentActions
            actions={assessmentActions}
            signedPrescription={signedPrescription}
            labOrder={signedLabOrder}
            imagingOrder={signedImageOrder}
            physicianOrder={signedPhysicianOrder}
            onEditPrescription={() => setOpenRX(true)}
            onEditLab={() => setOpenLabOrders(true)}
            onEditImaging={() => setOpenImageOrders(true)}
            onEditPhysician={() =>
              setOpenPhysicianOrders(true)
            }
          />

          <AssessmentPlanSection
            value={soapText.AssessmentPlan}
            diagnoses={selectedICD10Diagnoses}
            route={route}
            onChange={value =>
              handleSoapChange(
                'AssessmentPlan',
                value
              )
            }
            onOpenDiagnosis={() =>
              setOpenICD10Diagnosis(true)
            }
            onOpenTemplate={() => {
              setActiveOrderTemplateType(null);
              setOpenAssessmentTemplates(true);
            }}
            onRouteChange={handleRouteChange}
          />

          <SoapFooterActions
            disabled={!isDirty}
            onSave={handleSave}
            onSaveAndSign={handleSaveAndSign}
          />
        </Stack>

        <SoapEntryDialogs
          state={dialogState}
          handlers={dialogHandlers}
        />
      </Box>
    </LocalizationProvider>
  );
};

SoapEntry.propTypes = {
  isPast: PropTypes.bool,
  setPast: PropTypes.func,
  onSaveEncounter: PropTypes.func,
  onSignEncounter: PropTypes.func
};

SoapEntry.defaultProps = {
  isPast: false,
  setPast: () => {},
  onSaveEncounter: () => {},
  onSignEncounter: () => {}
};

export default SoapEntry;